// ─── Exportación a PDF · Plan de Prevención y Control de la CAI ───────────
//
// Genera el PDF del PPCAI a partir del plan almacenado en cc_planes_iniciales.
// Construye un HTML editorial con las 7 secciones del criterio SyB 2 del
// Manual BREEAM ES NC 2015 y lo envía al pdf-server (Puppeteer) que ya
// está desplegado en Hetzner y expuesto en /pdf/render-pdf.
//
// Estilos: DM Sans (ya usado en la app) + paleta BREEAM (verde #3D6764).
// Layout A4 con portada + secciones numeradas + pie de página.

import { saveAs } from 'file-saver'

const FRECUENCIAS = {
  diaria: 'Diaria', semanal: 'Semanal', quincenal: 'Quincenal',
  mensual: 'Mensual', por_hito: 'Por hito constructivo', '': 'Sin definir',
}

function esc(s) {
  if (s === null || s === undefined) return ''
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

function nl2br(s) {
  return esc(s).replace(/\n/g, '<br/>')
}

function bullets(arr) {
  if (!arr || arr.length === 0) return ''
  return `<ul>${arr.map(x => `<li>${esc(x)}</li>`).join('')}</ul>`
}

function kv(label, value) {
  return `<tr><th>${esc(label)}</th><td>${value ? nl2br(value) : '—'}</td></tr>`
}

// ═══════════════════════════════════════════════════════════════════════
//   HTML del PPCAI
// ═══════════════════════════════════════════════════════════════════════
function buildPpcaiHtml({ plan, projectName, contratistaPrincipal, ELEMENTOS_MANUAL, CATEGORIAS_PRODUCTOS, SMACNA_APARTADOS }) {
  const cj = plan.contenido_json || {}
  const ca = cj.criterios_adicionales || {}
  const pc = cj.post_construccion || {}
  const today = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })
  const puntosLabel = {
    1: '1 punto · solo el PPCAI implementado',
    2: '2 puntos · PPCAI + cumplimiento Tabla 13 productos',
    3: '3 puntos · PPCAI + Tabla 13 + medición post-construcción',
  }[cj.puntos_objetivo] || `${cj.puntos_objetivo || 1} punto(s)`

  // ─── Estilos ─────────────────────────────────────────────────────────
  const styles = `
    @page { size: A4; margin: 22mm 18mm 22mm 18mm; }
    * { box-sizing: border-box; }
    body {
      font-family: 'DM Sans', 'Segoe UI', system-ui, sans-serif;
      color: #3B2F2F; font-size: 10.5pt; line-height: 1.5;
      margin: 0; padding: 0;
    }
    h1, h2, h3, h4 { font-family: 'DM Sans', sans-serif; color: #3D6764; margin: 0; }
    h1 { font-size: 18pt; font-weight: 700; margin: 24pt 0 12pt; letter-spacing: -0.01em; }
    h2 { font-size: 13pt; font-weight: 600; margin: 20pt 0 8pt; }
    h3 { font-size: 11pt; font-weight: 600; margin: 14pt 0 6pt; color: #6CB43F; text-transform: uppercase; letter-spacing: 0.03em; }
    p { margin: 6pt 0; }
    ul { margin: 6pt 0 10pt; padding-left: 18pt; }
    li { margin: 3pt 0; }
    table.kv {
      width: 100%; border-collapse: collapse; margin: 8pt 0 16pt;
      font-size: 10pt;
    }
    table.kv th, table.kv td {
      border: 1px solid #DDD5C8; padding: 6pt 9pt; text-align: left; vertical-align: top;
    }
    table.kv th {
      background: #F5F0EA; font-weight: 600; width: 32%; color: #3B2F2F;
    }
    .cover {
      page-break-after: always;
      height: 100%; padding-top: 60mm; text-align: center;
    }
    .cover-title {
      font-family: 'DM Sans', sans-serif; font-size: 26pt; font-weight: 700;
      color: #3D6764; letter-spacing: -0.02em; line-height: 1.15;
      margin-bottom: 12pt;
    }
    .cover-subtitle {
      font-size: 13pt; color: #7A6A5A; font-style: italic;
      margin-bottom: 40pt;
    }
    .cover-meta { font-size: 12pt; margin: 4pt 0; }
    .cover-meta strong { color: #3B2F2F; }
    .cover-version {
      margin-top: 30pt; font-size: 10pt; color: #7A6A5A; font-style: italic;
    }
    .haus-brand {
      position: fixed; bottom: 8mm; left: 0; right: 0; text-align: center;
      font-size: 9pt; color: #7A6A5A;
    }
    .section {
      page-break-inside: auto;
    }
    .intro-note {
      background: #FBF8F2; border-left: 3px solid #C9A961;
      padding: 8pt 12pt; font-size: 9.5pt; color: #3B2F2F;
      margin: 10pt 0 18pt; border-radius: 2px;
    }
    .element-block {
      border: 1px solid #DDD5C8; border-radius: 4px;
      padding: 12pt 14pt; margin: 10pt 0 14pt;
      page-break-inside: avoid;
      background: #FDFCF9;
    }
    .element-ref {
      font-size: 9pt; color: #7A6A5A; font-style: italic; margin-bottom: 6pt;
    }
    .checkbox { font-size: 11pt; margin-right: 4pt; }
    .checkbox.on { color: #6CB43F; font-weight: 700; }
    .checkbox.off { color: #7A6A5A; }
    .category {
      border: 1px solid #DDD5C8; border-radius: 4px;
      padding: 10pt 14pt; margin: 8pt 0 12pt;
      page-break-inside: avoid;
    }
    .category-title {
      font-size: 11pt; font-weight: 600; color: #3B2F2F;
      margin-bottom: 6pt;
    }
    .category-code {
      display: inline-block; padding: 2pt 7pt; background: #3D6764;
      color: white; border-radius: 3px; margin-right: 6pt;
      font-size: 9pt; font-weight: 700;
    }
    .signature-block {
      margin-top: 30pt;
    }
    .signature-line {
      border-bottom: 1px solid #3B2F2F; width: 60%;
      margin: 40pt 0 4pt;
    }
    .no-aplica {
      font-size: 9.5pt; color: #7A6A5A; font-style: italic;
      margin: 6pt 0;
    }
  `

  // ─── Portada ─────────────────────────────────────────────────────────
  const portada = `
    <div class="cover">
      <div class="cover-title">Plan de Prevención y Control de la Calidad del Aire Interior</div>
      <div class="cover-subtitle">Criterio SyB 2 · BREEAM ES Nueva Construcción 2015</div>
      <div class="cover-meta"><strong>Proyecto:</strong> ${esc(projectName || '—')}</div>
      <div class="cover-meta"><strong>Contratista principal:</strong> ${esc(contratistaPrincipal?.nombre || '—')}${contratistaPrincipal?.rol ? ` <span style="color:#7A6A5A">(${esc(contratistaPrincipal.rol)})</span>` : ''}</div>
      <div class="cover-meta"><strong>Puntos objetivo:</strong> ${esc(puntosLabel)}</div>
      <div class="cover-version">Versión ${esc(plan.version)} · Estado: ${esc(plan.estado)} · ${esc(today)}</div>
      <div class="haus-brand">HAUS Healthy Buildings · Consultoría BREEAM ES</div>
    </div>
  `

  // ─── Sección 1 · Datos generales ─────────────────────────────────────
  const seccion1 = `
    <section class="section">
      <div class="intro-note">
        Documento elaborado siguiendo la estructura oficial del Manual Técnico BREEAM ES Nueva Construcción 2015
        (IPC-BREEAM-01-12 · Edición 02 · 31/03/2017), criterio SyB 2 · Calidad del aire interior (páginas 70-81 del manual).
      </div>
      <h1>1. Datos generales del plan</h1>
      <table class="kv">
        ${kv('Objetivo del plan', cj.objetivo)}
        ${kv('Ámbito de aplicación', cj.ambito)}
        ${kv('Cronograma de implementación', cj.cronograma)}
        ${kv('Puntos del criterio pretendidos', puntosLabel)}
        ${kv('Contratista responsable', contratistaPrincipal?.nombre)}
      </table>
    </section>
  `

  // ─── Sección 2 · Los 5 elementos oficiales ───────────────────────────
  const seccion2Blocks = (cj.elementos_manual || []).map((el, i) => {
    const elDef = ELEMENTOS_MANUAL[i] || {}
    const sel = el.medidas_seleccionadas || []
    const medidasSeleccionadas = sel.map(idx => (elDef.medidas || [])[idx]).filter(Boolean)
    const adicionales = (el.medidas_adicionales || '').split('\n').map(x => x.trim()).filter(Boolean)

    return `
      <div class="element-block">
        <h2>2.${esc(el.codigo)}. ${esc(elDef.titulo || el.titulo)}</h2>
        <div class="element-ref">${esc(elDef.referencia_manual || '')}</div>
        ${medidasSeleccionadas.length > 0 ? `
          <h3>Medidas comprometidas</h3>
          ${bullets(medidasSeleccionadas)}
        ` : `<p class="no-aplica">Sin medidas comprometidas para este elemento.</p>`}
        ${adicionales.length > 0 ? `
          <h3>Medidas adicionales del proyecto</h3>
          ${bullets(adicionales)}
        ` : ''}
        ${(el.responsable || el.frecuencia_control) ? `
          <table class="kv">
            ${kv('Responsable de la aplicación', el.responsable)}
            ${kv('Frecuencia de control', FRECUENCIAS[el.frecuencia_control] || el.frecuencia_control)}
          </table>
        ` : ''}
      </div>
    `
  }).join('')

  const seccion2 = `
    <section class="section">
      <h1>2. Los cinco elementos oficiales del PPCAI</h1>
      <p>Para obtener el primer punto del criterio SyB 2, el pliego oficial del manual (criterio 1) exige que el PPCAI aborde los cinco elementos siguientes (a-e). Se recogen las medidas que el contratista se compromete a aplicar en obra.</p>
      ${seccion2Blocks}
    </section>
  `

  // ─── Sección 3 · Criterios adicionales del punto 1 ───────────────────
  const dh = ca.distancias_hvac || {}
  const dv = ca.diseno_ventilacion || {}
  const sc = ca.sensores_co2 || {}
  const hayCriteriosAdicionales = dh.aplica || dv.cumple_une13779 || sc.aplica

  const seccion3 = hayCriteriosAdicionales ? `
    <section class="section">
      <h1>3. Criterios adicionales del punto 1 del manual</h1>
      ${dh.aplica ? `
        <h2>3.1. Distancias mínimas en el sistema HVAC</h2>
        <table class="kv">
          ${kv('Aplica al proyecto', 'Sí · edificio climatizado')}
          ${kv('Distancia > 10 m entre tomas y salidas de aire', dh.tomas_salidas_10m ? 'Sí' : 'No')}
          ${kv('Distancia > 20 m entre tomas y fuentes contaminación exterior', dh.tomas_fuentes_20m ? 'Sí' : 'No')}
          ${kv('Justificación', dh.justificacion)}
        </table>
      ` : ''}
      ${dv.cumple_une13779 ? `
        <h2>3.2. Diseño de ventilación conforme a UNE-EN 13779:2008</h2>
        <table class="kv">
          ${kv('Cumple UNE-EN 13779:2008', 'Sí')}
          ${kv('Caudales de aire exterior verificados', dv.caudales_verificados)}
          ${kv('Referencia del cálculo', dv.referencia_calculo)}
        </table>
      ` : ''}
      ${sc.aplica ? `
        <h2>3.3. Sensores de CO₂ en zonas de ocupación elevada e impredecible</h2>
        <table class="kv">
          ${kv('Aplica al proyecto', 'Sí')}
          ${kv('Zonas equipadas', (sc.zonas || []).join(', '))}
          ${kv('Conectados al sistema de ventilación', sc.conectado_ventilacion ? 'Sí · regulación automática del caudal' : 'No')}
          ${kv('Justificación', sc.justificacion)}
        </table>
      ` : ''}
    </section>
  ` : ''

  // ─── Sección 4 · Tabla 13 productos ──────────────────────────────────
  const aplicables = (cj.productos_por_categoria || []).filter(c => c.aplica)
  const noAplicables = (cj.productos_por_categoria || []).filter(c => !c.aplica)

  const seccion4 = `
    <section class="section">
      <h1>4. Tabla 13 · Productos con requisitos COV / formaldehído</h1>
      <p>Las 8 categorías A-H reproducidas del Manual BREEAM ES NC 2015 (páginas 77-79). Para obtener el segundo punto del criterio SyB 2, todos los productos instalados de las categorías aplicables deben cumplir estos requisitos.</p>
      ${aplicables.map(cat => `
        <div class="category">
          <div class="category-title">
            <span class="category-code">${esc(cat.codigo)}</span>
            ${esc(cat.label)}
          </div>
          <table class="kv">
            ${cat.criterio_comportamiento ? kv('Criterio de comportamiento', cat.criterio_comportamiento) : ''}
            ${Array.isArray(cat.normas_comportamiento) && cat.normas_comportamiento.length ? `<tr><th>Normas de comportamiento</th><td>${bullets(cat.normas_comportamiento)}</td></tr>` : ''}
            ${Array.isArray(cat.normas_ensayo) && cat.normas_ensayo.length ? `<tr><th>Normas de ensayo</th><td>${bullets(cat.normas_ensayo)}</td></tr>` : ''}
            ${cat.confirmacion_fabricante && cat.confirmacion_fabricante !== 'No aplicable' ? kv('Confirmación adicional del fabricante', cat.confirmacion_fabricante) : ''}
            ${cat.notas && cat.notas.trim() ? kv('Productos concretos seleccionados', cat.notas) : ''}
          </table>
        </div>
      `).join('')}
      ${noAplicables.length ? `
        <h3>Categorías que no aplican al proyecto</h3>
        ${bullets(noAplicables.map(c => `Categoría ${c.codigo} · ${c.label}`))}
      ` : ''}
    </section>
  `

  // ─── Sección 5 · Compromiso post-construcción ────────────────────────
  const compromisos = [
    { key: 'punto_2_productos', label: 'Punto 2 · Todos los productos instalados de las categorías A-H aplicables cumplen la Tabla 13' },
    { key: 'punto_3a_covt', label: 'Punto 3.a · Medición post-construcción de COVT · umbral < 300 μg/m³ durante 8 h' },
    { key: 'punto_3b_formaldehido', label: 'Punto 3.b · Medición post-construcción de formaldehído · umbral ≤ 100 μg/m³ durante 30 min (OMS)' },
    { key: 'ejemplar_formaldehido', label: 'Nivel ejemplar · Formaldehído ≤ 10 μg/m³' },
    { key: 'ejemplar_covt', label: 'Nivel ejemplar · COVT ≤ 60 μg/m³' },
  ]

  const seccion5 = `
    <section class="section">
      <h1>5. Compromiso post-construcción</h1>
      <p>Objetivos de puntuación del criterio SyB 2 comprometidos por el contratista y el equipo de proyecto:</p>
      <ul style="list-style:none; padding-left:0">
        ${compromisos.map(c => `
          <li style="margin: 6pt 0">
            <span class="checkbox ${pc[c.key] ? 'on' : 'off'}">${pc[c.key] ? '☑' : '☐'}</span>
            ${esc(c.label)}
          </li>
        `).join('')}
      </ul>
      ${Array.isArray(pc.metodos_ensayo) && pc.metodos_ensayo.length ? `
        <h3>Métodos de ensayo aplicables</h3>
        ${bullets(pc.metodos_ensayo)}
      ` : ''}
      ${pc.laboratorio ? `<h3>Laboratorio de ensayo</h3><p>${esc(pc.laboratorio)}</p>` : ''}
      ${pc.compromiso_reduccion ? `
        <h3>Compromiso en caso de superar los umbrales</h3>
        <p>${nl2br(pc.compromiso_reduccion)}</p>
      ` : ''}
    </section>
  `

  // ─── Sección 6 · Medidas SMACNA (opcional) ───────────────────────────
  const seccion6 = cj.aplica_smacna ? `
    <section class="section">
      <h1>6. Medidas de gestión de obra (SMACNA)</h1>
      <p>Los apartados siguientes son un requisito adicional del pliego del proyecto, complementarios al PPCAI oficial del manual.</p>
      ${(cj.medidas_smacna || []).map((ap, i) => {
        const apDef = SMACNA_APARTADOS[i] || {}
        const sel = ap.medidas_seleccionadas || []
        const medidasSeleccionadas = sel.map(idx => (apDef.medidas || [])[idx]).filter(Boolean)
        const adicionales = (ap.medidas_adicionales || '').split('\n').map(x => x.trim()).filter(Boolean)
        return `
          <div class="element-block">
            <h2>6.${esc(ap.codigo)}. ${esc(apDef.titulo || ap.titulo)}</h2>
            ${medidasSeleccionadas.length ? bullets(medidasSeleccionadas) : `<p class="no-aplica">Sin medidas comprometidas para este apartado.</p>`}
            ${adicionales.length > 0 ? `<h3>Medidas adicionales del proyecto</h3>${bullets(adicionales)}` : ''}
          </div>
        `
      }).join('')}
    </section>
  ` : ''

  // ─── Sección 7 · Firmante ────────────────────────────────────────────
  const seccion7 = `
    <section class="section">
      <h1>7. Firmante técnico del plan</h1>
      <table class="kv">
        ${kv('Nombre y apellidos', plan.firmante_nombre)}
        ${kv('Cargo', plan.firmante_cargo)}
        ${kv('Email de contacto', plan.firmante_email)}
        ${kv('Fecha de firma', plan.firma_fecha)}
      </table>
      <div class="signature-block">
        <p><strong>Firma:</strong></p>
        <div class="signature-line"></div>
      </div>
    </section>
  `

  // ─── Sección 8 · Notas del asesor (opcional) ─────────────────────────
  const seccion8 = cj.notas_asesor && cj.notas_asesor.trim() ? `
    <section class="section">
      <h1>8. Notas del asesor BREEAM</h1>
      <p>${nl2br(cj.notas_asesor)}</p>
    </section>
  ` : ''

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>PPCAI · ${esc(projectName || '')}</title>
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
  <style>${styles}</style>
</head>
<body>
  ${portada}
  ${seccion1}
  ${seccion2}
  ${seccion3}
  ${seccion4}
  ${seccion5}
  ${seccion6}
  ${seccion7}
  ${seccion8}
</body>
</html>`
}

// ═══════════════════════════════════════════════════════════════════════
//   EXPORT · llama al pdf-server (Puppeteer) y descarga el PDF
// ═══════════════════════════════════════════════════════════════════════
export async function exportPpcaiToPdf({
  plan, projectName, contratistaPrincipal,
  ELEMENTOS_MANUAL, CATEGORIAS_PRODUCTOS, SMACNA_APARTADOS,
}) {
  const html = buildPpcaiHtml({
    plan, projectName, contratistaPrincipal,
    ELEMENTOS_MANUAL, CATEGORIAS_PRODUCTOS, SMACNA_APARTADOS,
  })

  const isProd = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
  const baseUrl = isProd ? '/pdf' : 'http://localhost:3010'
  const proyectoSlug = (projectName || 'proyecto').replace(/\s+/g, '_')
  const fecha = new Date().toISOString().split('T')[0]
  const filename = `PPCAI_${proyectoSlug}_v${plan.version}_${fecha}.pdf`

  const resp = await fetch(`${baseUrl}/render-pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      html,
      filename,
      options: {
        projectName: projectName || 'Proyecto BREEAM',
        headerTitle: 'PPCAI · SyB 2',
        // El HTML ya trae su propia portada · dejamos que Puppeteer añada
        // la cabecera y pie estándar del servidor con la numeración de páginas.
        hideHeaderFooter: false,
      },
    }),
  })

  if (!resp.ok) {
    let msg = `Error HTTP ${resp.status}`
    try { const j = await resp.json(); if (j.error) msg = j.error } catch (_) {}
    throw new Error(msg)
  }
  const blob = await resp.blob()
  saveAs(blob, filename)
}
